import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread_count: 0 });
  const dropRef = useRef(null);

  const load = async () => {
    if (!user) return;
    try {
      const { data: d } = await api.get("/notifications/mine?limit=20");
      setData(d);
    } catch {}
  };

  useEffect(() => {
    if (!user) return;
    load();
    const h = setInterval(load, 45000);
    return () => clearInterval(h);
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    const close = (e) => { if (open && dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);

  const markAll = async () => {
    try {
      await api.post("/notifications/mark-read", {});
      load();
    } catch {}
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropRef}>
      <button
        type="button"
        data-testid="notification-bell-btn"
        aria-label="Notifications"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); if (!open) load(); }}
        className="relative text-white/80 hover:text-white p-2"
      >
        <Bell className="w-5 h-5" />
        {data.unread_count > 0 && (
          <span
            data-testid="notification-bell-badge"
            className="absolute top-0 right-0 bg-tv-live text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center"
          >
            {data.unread_count > 9 ? "9+" : data.unread_count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 top-10 w-80 sm:w-96 tv-glass rounded-xl shadow-2xl border border-white/10 overflow-hidden z-[80]"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="text-xs uppercase tracking-widest text-tv-accent font-bold">Notifications</div>
            {data.unread_count > 0 && (
              <button data-testid="notification-mark-all" onClick={markAll} className="text-xs text-white/70 hover:text-white">Mark all read</button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {data.notifications.length === 0 ? (
              <div className="p-8 text-center text-white/50 text-sm">You're all caught up 🎉</div>
            ) : (
              data.notifications.map((n) => (
                <Link
                  key={n.id}
                  to={n.link || n.deep_link || "#"}
                  onClick={() => setOpen(false)}
                  data-testid={`notification-row-${n.id}`}
                  className={`flex gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/5 transition ${!n.read ? "bg-white/[0.02]" : ""}`}
                >
                  {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-tv-accent mt-2 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-semibold line-clamp-1">{n.title}</div>
                    {n.body && <div className="text-white/60 text-xs mt-0.5 line-clamp-2">{n.body}</div>}
                    <div className="text-white/30 text-[10px] mt-1">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
